import * as React from 'react';
import { isEmpty, forEach, isEqual, isNil } from 'lodash';

import { ClientDataApi } from 'configuration-schemas/confdefnView';
import { getValidValues } from '../StyleEditSection/StyleEditSection.client';
import { getUrl } from '../StyleEdit.utils';
import InputSuggest, {
  MultiselectInputSuggestProps,
  Suggestion,
} from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';
import { DividedColumnDetailsSectionInputParams } from './DividedColumnDetails.types';
import { errorToLoggingPayload } from 'src/services/loggingService';
import { toast } from 'react-toastify';
import ServiceContainer from 'src/ServiceContainer';
import { style } from 'typestyle';
import { AutocompleteCloseReason } from '@material-ui/lab';

export interface DropdownProps extends MultiselectInputSuggestProps {
  value: string;
  asCsv?: boolean;
  styleId: string;
  dataApi: ClientDataApi;
  dataQa: string;
  allowEmptyOption?: boolean;
  concatOptionValues?: boolean;
  renderIdOnly?: boolean;
  disabled?: boolean;
  inputParams?: DividedColumnDetailsSectionInputParams;
  handleDropdownChange: (selection: any) => void;
  // If true, we call handleOpen() on mount so the MUI Autocomplete is open immediately
  initiallyOpen?: boolean;
  onClose?: () => void;
}

const styles = style({
  marginBottom: '8px',
  $nest: {
    '.s5-MuiInputBase-root': {
      fontSize: '0.9rem !important',
    },
  },
});

const explicitProps = [
  'value',
  'multiSelect',
  'asCsv',
  'styleId',
  'dataApi',
  'dataQa',
  'allowEmptyOption',
  'concatOptionValues',
  'renderIdOnly',
  'handleDropdownChange',
  'inputParams',
  'initiallyOpen',
  'onClose',
  'fullWidthLabels',
];

/** Filter out any known props so we can safely spread the rest on a <div> if needed. */
function getFilteredProps(props: DropdownProps) {
  const filteredProps = {} as Record<string, unknown>;
  forEach(props, (val, key) => {
    if (!explicitProps.includes(key)) {
      filteredProps[key] = val;
    }
  });
  return filteredProps;
}

export interface DropdownState {
  loading: boolean;
  options: Suggestion[];
  selected?: Suggestion | Suggestion[] | string;
  loadedStyleId: string | null;
  open: boolean;
}

export default class DividedColumnDetailsDropdown extends React.Component<DropdownProps, DropdownState> {
  constructor(props: DropdownProps) {
    super(props);
    this.state = {
      loading: false,
      options: [],
      selected: undefined,
      loadedStyleId: null,
      open: false,
    };
  }

  componentDidMount() {
    // logic for style edit where the dropdown needs to open on render due to component switching
    if (this.props.initiallyOpen) {
      const initialSelected = this.getSelected([]);
      this.setState({ selected: initialSelected }, () => {
        this.handleOpen();
      });
      // every other case - fetch immediately as usual
    } else this.getData();
  }

  componentDidUpdate(prevProps: DropdownProps) {
    if (prevProps.styleId !== this.props.styleId) {
      this.getData();
    }

    if (!prevProps.value && this.props.value && this.state.options.length > 0) {
      this.setState({
        selected: this.getSelected(this.state.options),
      });
    } else if (!isEqual(prevProps.value, this.props.value)) {
      this.setState({
        selected: this.getSelected(this.state.options),
      });
    }
  }

  /** Called by MUI Autocomplete when user tries to open the dropdown. */
  handleOpen = () => {
    const { loadedStyleId } = this.state;
    const { styleId, multiSelect } = this.props;
    // if not loaded or style changed => fetch
    if (!loadedStyleId || loadedStyleId !== styleId) {
      this.getData();
    }
    this.setState({ open: true });
  };

  /** Called by MUI Autocomplete when user closes (ESC). */
  handleClose = (_event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
    this.setState({ open: false });
    if (this.props.onClose) {
      this.props.onClose();
    }
    const currentSelection = this.getSelected(this.state.options)
    if (this.props.multiSelect && !isEqual(currentSelection, this.state.selected)) {
      if (reason == 'escape') {
        return;
      }
      if (this.props.asCsv && this.props.multiSelect) {
        const selections = this.state.selected as unknown as Suggestion[];
        const final = {
          label: selections.map((s) => s.label).join(','),
          value: `{${selections.map((s) => s.value).join(',')}}`,
        };
        this.props.handleDropdownChange(final);
      } else {
        this.props.handleDropdownChange(this.state.selected);
      }
    }
  };

  getData() {
    const { dataApi, allowEmptyOption, concatOptionValues, renderIdOnly, styleId } = this.props;
    const url = getUrl(dataApi);

    this.setState({ loading: true });

    getValidValues(url, allowEmptyOption, concatOptionValues, false, renderIdOnly)
      .then((validValues) => {
        this.setState({
          options: validValues,
          selected: this.getSelected(validValues),
          loading: false,
          loadedStyleId: styleId,
        });
      })
      .catch((err: any) => {
        toast.error('An error occurred fetching the options for a dropdown');
        ServiceContainer.loggingService.error(`An error occurred: ${errorToLoggingPayload(err)}`, err.stack);
      })
      .finally(() => {
        this.setState({
          loading: false,
        });
      });
  }

  getSelected(options: Suggestion[]): Suggestion | string | Suggestion[] | undefined {
    const { value, multiSelect } = this.props;
    // TODO: We should be accepting value as array in cases of asCsv = false, but for now
    // value as csv false just means output is array, input is still csv. I'm sorry - </3 Mark
    if (multiSelect) {
      const matched = value.match(/{(.*)}/);
      let values: string[];

      if (matched) {
        const match = matched[1];
        // need to explicitly set empty selection to empty array instead of trying to split
        values = isEmpty(match) ? [] : match.split(',');
      } else {
        values = value.split(',');
      }

      const selectedOptions = options.filter((opt) => values.includes(opt.value as string));
      if (selectedOptions.length > 0) {
        return selectedOptions;
      }

      // in the case of a value of 'Undefined' we want to render the stripped value,
      // which is matched[1], instead of the raw value '{Undefined}' received from the server
      return matched && matched[0] === value ? matched[1] : value;
    } else {
      const found = options.find((opt) => opt.value === value);
      return found || value;
    }
  }

  handleMultiSelection = (selection: Suggestion[]) => {
    this.setState({
      selected: selection,
    });
  };

  handleSelection = (selection: Suggestion) => {
    // empty suggestion when cleared (this is to re-disable the submit after clear)
    const cleared = !selection || selection.value === '';
    if (cleared) {
      this.setState({ selected: undefined }, () => {
        this.props.handleDropdownChange(null);
      });
      return;
    }
    // normal route single select
    this.setState({ selected: selection });
    this.props.handleDropdownChange(selection);
  };

  render() {
    const { multiSelect = false, fullWidthLabels = false, dataQa, disabled, inputParams } = this.props;
    const { loading, options, selected, open } = this.state;

    const dropdownOptions = isNil(options) || isEmpty(options) ? [] : options;
    const selectedValue = !isEmpty(dropdownOptions) ? selected : undefined;

    const filteredProps = getFilteredProps(this.props);

    return (
      <div {...filteredProps} style={{ height: inputParams?.height }} className={styles}>
        <InputSuggest
          style={{ height: inputParams?.height }}
          validValues={dropdownOptions}
          autofocus={true}
          multiSelect={multiSelect}
          fullWidthLabels={fullWidthLabels}
          selected={isEmpty(selectedValue) ? undefined : selectedValue}
          onSelect={this.handleSelection}
          onMultiSelect={this.handleMultiSelection}
          disabled={!isNil(disabled) ? disabled : false}
          isLoading={loading}
          dataQa={dataQa}
          open={open}
          onOpen={this.handleOpen}
          onClose={this.handleClose}
          allowPortal={true}
        />
      </div>
    );
  }
}
